import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import type { LateArrivalMonthSummary } from '../types/lateArrivalConfiguration';
import { formatAttendanceDate, formatMinutes } from '../utils/dateTime';
import { formatLeaveDays, ordinal } from '../utils/lateArrivalRules';

type Props = {
  summary: LateArrivalMonthSummary;
  palette: ColorPalette;
};

export function LateArrivalMonthCard({ summary, palette }: Props) {
  if (!summary.isEnabled && summary.lateCount === 0) return null;
  const next = summary.nextRule;
  return (
    <View
      accessibilityLabel="Late arrivals this month"
      style={[styles.block, { borderBottomColor: palette.border }]}
    >
      <Text style={[styles.title, { color: palette.textPrimary }]}>
        Late Arrivals This Month
      </Text>
      <Text style={[styles.value, { color: palette.textPrimary }]}>
        {summary.lateCount} late arrival{summary.lateCount === 1 ? '' : 's'}
      </Text>
      {summary.leaveDeductedDays > 0 ? (
        <Text style={[styles.caption, { color: palette.textMuted }]}>
          Leave deducted: {formatLeaveDays(summary.leaveDeductedDays)}
        </Text>
      ) : null}
      {summary.lopDays > 0 ? (
        <Text style={[styles.caption, { color: palette.error }]}>
          LOP: {formatLeaveDays(summary.lopDays)}
        </Text>
      ) : null}
      {summary.items.map(item => (
        <Text
          key={item.lateArrivalDeductionId ?? `${item.attendanceDate}-${item.occurrenceNumber}`}
          style={[styles.caption, { color: palette.textMuted }]}
        >
          {ordinal(item.occurrenceNumber)} ·{' '}
          {item.attendanceDate ? formatAttendanceDate(item.attendanceDate) : ''} ·{' '}
          {item.leaveDeductedDays > 0
            ? `${formatLeaveDays(item.leaveDeductedDays)} ${item.leaveTypeName ?? ''}`.trim()
            : 'No leave deducted'}
          {item.lopDays > 0 ? ` · LOP ${formatLeaveDays(item.lopDays)}` : ''}
        </Text>
      ))}
      {summary.isEnabled && next ? (
        <Text style={[styles.caption, { color: palette.textMuted }]}>
          Next late arrival
          {summary.lateThresholdMinutes !== null
            ? ` (more than ${formatMinutes(summary.lateThresholdMinutes)} after shift start)`
            : ''}
          : {formatLeaveDays(next.deductionDays)} {next.leaveTypeName ?? ''}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    paddingVertical: spacing.md,
    gap: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: { ...typography.sectionTitle },
  value: { ...typography.body, fontWeight: '600' },
  caption: { ...typography.caption },
});
